/**
 * Shared helpers for processing thread recommendations from the convoai_homeThreads
 * GraphQL endpoint.
 */

import type { SpaceSource } from '@atlassian/rovo-spaces/ui/CreateSpaceModal/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A thread source as read by {@link extractSourcesFromThread}. Declared with `readonly` fields and an
 * optional Relay fragment-spread phantom so the Relay-generated `sources` type (and the REST
 * `RecommendedThreadSource`) are directly assignable — no cast needed at call sites. Only the fields
 * below are consumed; the phantom is ignored.
 */
export interface ThreadSource {
	readonly __typename: string;
	readonly ari?: string;
	readonly url?: string;
	readonly data?: { readonly __typename: string } | null;
	// Allows Relay-generated source types (which carry a ` $fragmentSpreads` phantom) to assign.
	readonly ' $fragmentSpreads'?: unknown;
}

export interface ThreadNode {
	id: string;
	title: string;
	sources: readonly ThreadSource[] | null;
}

// ---------------------------------------------------------------------------
// Source helpers
// ---------------------------------------------------------------------------

/** Derives sourceType from a first-party source's data __typename. */
export const getSourceTypeFromData = (dataTypename: string | undefined): string => {
	if (!dataTypename) {
		return 'unknown';
	}
	// ConvoAiHomeThreadsConfluenceData → confluence, ConvoAiHomeThreadsJiraData → jira, etc.
	const match = dataTypename.match(/ConvoAiHomeThreads(\w+)Data/);
	return match ? match[1].toLowerCase() : dataTypename.toLowerCase();
};

/** Derives sourceType from a URL's hostname. */
export const getSourceTypeFromUrl = (url: string): string => {
	try {
		const hostname = new URL(url).hostname;
		if (hostname.includes('atlassian.net') && url.includes('/browse/')) return 'jira';
		if (hostname.includes('atlassian.net') && url.includes('/wiki/')) return 'confluence';
		if (hostname.includes('bitbucket')) return 'bitbucket';
		if (hostname.includes('loom')) return 'loom';
		if (hostname.includes('github')) return 'github';
	} catch {
		// fall through
	}
	return 'unknown';
};

/** Derives a human-readable name from a source URL. */
export const getSourceName = (url: string, sourceType: string): string => {
	try {
		const urlObj = new URL(url);
		const path = urlObj.pathname;
		if (sourceType === 'jira') {
			const match = path.match(/\/browse\/([A-Z]+-\d+)/);
			return match ? match[1] : 'Jira issue';
		}
		if (sourceType === 'confluence') {
			const parts = path.split('/');
			const lastPart = parts[parts.length - 1];
			return lastPart ? decodeURIComponent(lastPart.replace(/\+/g, ' ')) : 'Confluence page';
		}
		if (sourceType === 'loom') {
			// Loom URLs look like /share/my-video-title-UUID or /share/UUID
			// Only extract a title if the slug has hyphens (i.e. it's a title slug, not a bare ID)
			const match = path.match(/\/share\/(.+)$/);
			if (match) {
				const slug = match[1];
				// Remove trailing 32-char hex UUID segment (e.g. "-abc123def456...")
				const withoutUuid = slug.replace(/-[a-f0-9]{32}$/, '');
				// Only use as a title if it still contains hyphens after UUID removal
				if (withoutUuid.includes('-')) {
					const title = withoutUuid.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
					return title || 'Loom video';
				}
			}
			return 'Loom video';
		}
		if (sourceType === 'bitbucket') {
			// Bitbucket PR URLs look like /workspace/repo/pull-requests/123
			const prMatch = path.match(/\/([^/]+)\/([^/]+)\/pull-requests\/(\d+)/);
			if (prMatch) {
				return `${prMatch[2]}#${prMatch[3]}`;
			}
			// Bitbucket repo URLs look like /workspace/repo
			const repoMatch = path.match(/\/([^/]+)\/([^/]+)\/?$/);
			if (repoMatch) {
				return repoMatch[2];
			}
			return 'Bitbucket';
		}
	} catch {
		// fall through
	}
	return url;
};

/**
 * Extracts source URLs and structured SpaceSource objects from a thread's sources.
 * Handles both first-party and third-party sources.
 */
export const extractSourcesFromThread = (
	sources: readonly ThreadSource[],
): {
	sourceUrls: string[];
	spaceSources: SpaceSource[];
} => {
	const sourceUrls: string[] = [];
	const spaceSources: SpaceSource[] = [];

	sources.forEach((source) => {
		const url =
			source.__typename === 'ConvoAiHomeThreadsFirstPartySource' ||
			source.__typename === 'ConvoAiHomeThreadsThirdPartySource'
				? (source.url ?? '')
				: '';

		if (url) {
			sourceUrls.push(url);
		}

		if (source.__typename === 'ConvoAiHomeThreadsFirstPartySource' && source.ari) {
			const sourceType = getSourceTypeFromData(source.data?.__typename);
			spaceSources.push({
				ari: source.ari,
				url,
				sourceType,
				conversationIds: [],
				name: getSourceName(url, sourceType),
			});
		} else if (source.__typename === 'ConvoAiHomeThreadsThirdPartySource' && url) {
			const sourceType = getSourceTypeFromUrl(url);
			spaceSources.push({
				ari: url,
				url,
				sourceType,
				conversationIds: [],
				name: getSourceName(url, sourceType),
			});
		}
	});

	return { sourceUrls, spaceSources };
};
